/**
 * THE SCHEDULE OF RATES.
 *
 * 545 items were seeded on 14-09-2026, and the number only goes up. Two things
 * follow from that, and this screen exists to do both.
 *
 * ---------------------------------------------------------------------------
 * 1. IT HAS TO BE EDITABLE
 *
 * Every rate in the seed is marked INDICATIVE - they were written to give a
 * Procurement Manager something to negotiate against, not to be a price list.
 * The moment OVAL signs a contract at a real rate, the guide is stale, and
 * needing a database migration to correct a number is how a master becomes
 * something nobody trusts and everybody works around.
 *
 * Board 14-09-2026: Procurement Managers, the Head of Procurement and the
 * Project Head may add and correct items. The CMD, the CXO and Project Managers
 * read. The buttons below follow that list; work_items_write and
 * work_items_write_project_head enforce it.
 *
 * ---------------------------------------------------------------------------
 * 2. IT CANNOT BE A DROP-DOWN
 *
 * A <select> of 545 options is a scroll bar with no search, and the builder had
 * exactly that. So this same screen doubles as the PICKER: the builder sends
 * you here with ?pick=<line>&back=<where>, you search, you click a row, the row
 * flashes green so you can see which one you took, and you are put back on the
 * form with the item filled in.
 *
 * One screen rather than a modal, deliberately. The picker is the register -
 * the same search, the same columns, the same measurement rule in the last
 * column, which is the thing you actually choose between two similar items on.
 * A modal would have been a second, worse copy of it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Check, HardHat, Pencil, Plus, Search } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { rupees } from '@/lib/format'
import {
  Alert, Card, EmptyState, Field, Input, Loading, Modal, Select,
  Textarea, Th, sortRows, useSort, useToast,
} from '@/components/ui'
import { serviceCan } from '../nav'
import { SorExcelButtons, useIsSuperadmin } from '../components/SorExcel'

const n = (v: unknown) => Number(v ?? 0) || 0

export interface WiRow {
  id: string; item_code: string; item_name: string; uom: string
  category: string | null; sub_category: string | null
  default_nature: string; sac_code: string | null
  gst_rate_percent: number; standard_rate: number | null
  measurement_rule: string | null; description: string | null; is_active: boolean
  /** 0214, Plant & Equipment Hire only: how the guide rate is quoted. */
  hire_rate_basis?: string | null; hire_quoted_rate?: number | null
  /** 0220: rented by the piece and logged on delivery and return challans. */
  hire_by_pieces?: boolean
}

const NATURE = [
  { value: 'WORKS_CONTRACT', label: 'Works contract — he brings the material (SAC 9954)' },
  { value: 'PURE_LABOUR', label: 'Pure labour — OVAL supplies the material (SAC 9954)' },
  { value: 'MANPOWER_SUPPLY', label: 'Manpower supply (SAC 9985)' },
  // 0214. Paid per day on site from the hire log sheet, not measured.
  { value: 'EQUIPMENT_HIRE', label: 'Equipment / vehicle hire — paid per day on site' },
]

/**
 * THE TRADES A CIVIL CONTRACT IS DIVIDED INTO.
 *
 * The first 23 are the ones the 545 seeded items already use, and they came
 * from how the work is actually let on OVAL's jobs — schools, roads, drains,
 * malls, hospitals, lakes, parks, gates. The remainder are trades OVAL has not
 * yet written a rate for but which appear on the same kinds of job and would
 * otherwise be typed four different ways by four different buyers.
 *
 * This is a STARTING list, not a closed one. The dropdown below shows these
 * PLUS whatever the table already contains, so a trade added once appears for
 * everybody afterwards and the list grows by being used. "Add a new trade…" at
 * the foot is how the first one gets in.
 *
 * The whole point is that "Masonry" is picked, not typed. Free text is how a
 * master ends up with Masonry, masonry, Mason work and Brick Work as four
 * different trades, and no report can ever group them.
 */
export const TRADES = [
  // In the order the work happens on site.
  'Site Clearance & Dismantling',
  'Survey, Testing & Investigation',
  'Earthwork',
  'Piling & Deep Foundations',
  'Plain & Reinforced Concrete',
  'Formwork & Shuttering',
  'Reinforcement & Structural Steel',
  'Masonry',
  'Plastering & POP',
  'Waterproofing',
  'Roofing & Sheeting',
  'Flooring, Tiling & Cladding',
  'Doors, Windows & Joinery',
  'Aluminium, Glazing & Façade',
  'False Ceiling & Partitions',
  'Painting & Protective Coating',
  'Plumbing & Sanitary',
  'Water Supply & Pipeline',
  'Drainage, Sewerage & Stormwater',
  'Electrical & Conduiting',
  'Fire Fighting & Protection',
  'HVAC & Ventilation',
  'Lifts, Escalators & Conveying',
  'Solar & Renewables',
  'Roads & Pavements',
  'Compound Wall, Gates & Fencing',
  'Landscaping, Parks & Horticulture',
  'Lake & Waterbody Works',
  'Signage, Graphics & Wayfinding',
  'Structural Repair & Rehabilitation',
  'Safety, Barricading & Traffic Management',
  'Cleaning & Housekeeping',
  'Temporary Works & Miscellaneous',
  'Manpower Day Rates',
  // 0214: machines and vehicles on hire.
  'Plant & Equipment Hire',
]

/**
 * How the item is let, within its trade. Four words, used consistently, are
 * worth more than forty used once each.
 */
export const SUB_TRADES = [
  'Labour only',
  'With material',
  'Supply only',
  'Day rate',
  'Dismantling',
  'Repair & rehabilitation',
  'Testing & commissioning',
  // 0214, for Plant & Equipment Hire.
  'With operator',
  'Without operator',
]

/** The marker that turns a dropdown into a text box. */
const ADD_NEW = '__new__'

/** Blank form state. */
const emptyForm = (): WiRow => ({
  id: '', item_code: '', item_name: '', uom: '', category: '', sub_category: '',
  default_nature: 'WORKS_CONTRACT', sac_code: '', gst_rate_percent: 18,
  standard_rate: null, measurement_rule: '', description: '', is_active: true,
})

/**
 * The item code, derived from what the person types rather than taken raw.
 *
 * Every seeded item reads WI-CEIL-FC-COVE-L: the WI- prefix, the trade, the
 * item, and -L or -C for labour-only or composite. The id and the item_code are
 * THE SAME STRING on all 545 of them, and work order lines point at the id.
 *
 * Testing on 14-09-2026 found the first hand-added item sitting in the register
 * as TEST-DEMO-01 among 545 rows that all began WI-. It was not wrong - the id
 * underneath was WI-TEST-DEMO-01 - but a Code column where one row does not
 * match the other 545 is a master nobody trusts, and a sort by code put it
 * miles from its neighbours.
 *
 * So the prefix is added here, once, and the same value is written to BOTH
 * columns. Typing "WI-" yourself is harmless: the prefix is not doubled.
 */
export function idFromCode(code: string): string {
  const slug = code.trim().toUpperCase()
    .replace(/[^A-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/^WI-/, '')
  return slug ? `WI-${slug}` : ''
}

export default function ScheduleOfRates() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()

  // ---- picker mode --------------------------------------------------------
  const pickFor = params.get('pick')             // which builder line is waiting
  const backTo = params.get('back')              // where to put the person back
  const picking = Boolean(pickFor && backTo)
  const [flash, setFlash] = useState<string | null>(null)

  const [term, setTerm] = useState('')
  const { sort, toggle } = useSort('item_name', 'asc')
  const [editing, setEditing] = useState<WiRow | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // "Add a new trade…" turns the dropdown into a text box for that one field.
  const [newTrade, setNewTrade] = useState(false)
  const [newSub, setNewSub] = useState(false)

  const { data: rows, loading, refresh } = useQuery<WiRow>(
    'work_items',
    () => supabase.from('work_items').select('*'),
    [], { cacheable: false })

  /**
   * The SAC master — a DIFFERENT master from this one, on the board's
   * instruction. This schedule says what the work is and what it costs; the
   * SAC master says what the tax authority calls it.
   *
   * is_active excludes '9954' and '9985', which are four-digit headings kept
   * only so the historic seed migration still replays.
   */
  const { data: sacs } = useQuery<any>('sac_codes',
    () => supabase.from('sac_codes').select('code, description, gst_rate_percent')
      .eq('is_active', true).order('code'),
    [], { cacheable: false, idField: 'code' })

  /** Choosing a SAC carries its GST slab across, where the master states one. */
  function pickSac(code: string) {
    const rate = (sacs as any[]).find((x) => x.code === code)?.gst_rate_percent
    setEditing((e: any) => (e ? {
      ...e, sac_code: code,
      ...(rate == null ? {} : { gst_rate_percent: Number(rate) }),
    } : e))
  }

  const { data: uoms } = useQuery<any>('uoms',
    () => supabase.from('uoms').select('code, description, uom_class, sort_order')
      .eq('is_active', true).order('uom_class').order('sort_order').order('code'),
    [], { cacheable: false, idField: 'code' })

  const uomOptions = useMemo(
    () => (uoms as any[]).map((u) => ({
      value: u.code, label: u.description ? `${u.code} — ${u.description}` : u.code })),
    [uoms])

  /**
   * The trade list on offer: the canonical list above, UNION whatever is
   * already in the table, sorted. So a trade somebody added last month is on
   * everybody's dropdown this month, and the list can only get more complete.
   */
  const tradeOptions = useMemo(() => {
    const used = rows.map((r) => (r.category ?? '').trim()).filter(Boolean)
    return [...new Set([...TRADES, ...used])].sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }))
  }, [rows])

  /**
   * Sub-categories, narrowed to the chosen trade where the trade already has
   * some. Offering "Testing & commissioning" under Earthwork is noise; offering
   * what this trade actually uses is a shortcut.
   */
  const subTradeOptions = useMemo(() => {
    const trade = (editing?.category ?? '').trim()
    const inTrade = rows
      .filter((r) => !trade || (r.category ?? '').trim() === trade)
      .map((r) => (r.sub_category ?? '').trim()).filter(Boolean)
    return [...new Set([...SUB_TRADES, ...inTrade])].sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: t }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, editing?.category])

  const canEdit = serviceCan(profile?.role, 'manageWorkItems')
  // 0231: Excel export / import, for the System Administrator Mrinmoy Debnath only.
  const isSuper = useIsSuperadmin(profile?.id)

  const shown = useMemo(() => {
    const t = term.trim().toLowerCase()
    return sortRows(rows.filter((r) => !t
      || [r.item_code, r.item_name, r.category, r.sub_category, r.measurement_rule]
        .some((v) => String(v ?? '').toLowerCase().includes(t))),
      sort, { standard_rate: 'number', gst_rate_percent: 'number' })
  }, [rows, term, sort])

  // ---- choosing one, in picker mode ---------------------------------------
  //
  // The flash is not decoration. A 545-row table scrolled halfway down looks
  // identical the instant before and the instant after a click, and going
  // straight back to the form leaves the person unsure whether they took the
  // row they meant. Half a second of green, on the row itself, and then away.
  const choose = useCallback((item: WiRow) => {
    if (!picking || flash) return
    setFlash(item.id)
    window.setTimeout(() => {
      const join = backTo!.includes('?') ? '&' : '?'
      navigate(`${backTo}${join}picked=${encodeURIComponent(item.id)}`
               + `&line=${encodeURIComponent(pickFor!)}`)
    }, 500)
  }, [picking, flash, backTo, pickFor, navigate])

  // In picker mode the search box takes focus, because the person arrived here
  // to type a word, not to scroll.
  useEffect(() => {
    if (!picking) return
    const el = document.querySelector<HTMLInputElement>('input[data-sor-search]')
    el?.focus()
  }, [picking])

  // ---- saving -------------------------------------------------------------
  function openNew() {
    setErr(null); setIsNew(true); setNewTrade(false); setNewSub(false)
    setEditing(emptyForm())
  }
  function openEdit(r: WiRow) {
    setErr(null); setIsNew(false); setNewTrade(false); setNewSub(false)
    setEditing({ ...r })
  }

  const problems = useMemo(() => {
    if (!editing) return []
    const p: string[] = []
    if (!editing.item_code.trim()) p.push('An item code is needed — it becomes the reference the work order prints.')
    if (!editing.item_name.trim()) p.push('Say what the item is, in the words an engineer would use.')
    if (!editing.uom.trim()) p.push('Choose the unit it is measured in.')
    if (!editing.category?.trim())
      p.push('Choose the trade. It is what groups the item in the register and on every report.')
    if (!editing.measurement_rule?.trim())
      p.push('Say how it is measured. This sentence is printed on the work order and it is what stops an argument becoming a dispute.')
    if (isNew && !idFromCode(editing.item_code)) p.push('That code has no letters or digits in it.')
    if (isNew && rows.some((r) => r.id === idFromCode(editing.item_code)))
      p.push(`${idFromCode(editing.item_code)} already exists. Edit that item rather than adding a second one.`)
    if (!editing.sac_code?.trim())
      p.push('Choose a SAC code. It is printed on the work order and an invoice without one is not a valid tax document.')
    if (n(editing.gst_rate_percent) < 0 || n(editing.gst_rate_percent) > 100)
      p.push('GST must be between 0 and 100.')
    // 0214: a hire item is billed per day on site, whatever it was quoted in.
    if (editing.default_nature === 'EQUIPMENT_HIRE') {
      if (editing.uom !== 'DAY')
        p.push('A hire item is paid per day on site, so its unit is DAY — even when it is quoted per month.')
      if (!editing.hire_rate_basis)
        p.push('Say whether the hire is usually quoted per month or per day.')
      if (n(editing.hire_quoted_rate) <= 0)
        p.push('Enter the quoted hire rate.')
    }
    return p
  }, [editing, isNew, rows])

  async function save() {
    if (!editing || problems.length) return
    setBusy(true); setErr(null)
    try {
      // The code and the id are the SAME string, on a new item and on an
      // edited one, exactly as all 545 seeded rows are. See idFromCode.
      const code = isNew ? idFromCode(editing.item_code) : editing.id
      const body = {
        item_code: code,
        item_name: editing.item_name.trim(),
        uom: editing.uom.trim(),
        category: editing.category?.trim() || null,
        sub_category: editing.sub_category?.trim() || null,
        default_nature: editing.default_nature,
        sac_code: editing.sac_code?.trim(),   // NOT NULL since 0131
        gst_rate_percent: n(editing.gst_rate_percent),
        standard_rate: editing.standard_rate === null || String(editing.standard_rate) === ''
          ? null : n(editing.standard_rate),
        measurement_rule: editing.measurement_rule?.trim() || null,
        description: editing.description?.trim() || null,
        is_active: editing.is_active,
        updated_at: new Date().toISOString(),
        // 0214: only a hire item carries these, and its guide rate is the
        // quoted rate turned into a day rate (a month is 30 days). Every other
        // item saves exactly the fields it always did.
        ...(editing.default_nature === 'EQUIPMENT_HIRE' ? {
          hire_rate_basis: editing.hire_rate_basis,
          hire_quoted_rate: n(editing.hire_quoted_rate),
          hire_by_pieces: Boolean(editing.hire_by_pieces),
          standard_rate: Math.round(n(editing.hire_quoted_rate)
            / (editing.hire_rate_basis === 'MONTH' ? 30 : 1) * 100) / 100,
        } : {}),
      }
      const { error } = isNew
        ? await supabase.from('work_items').insert({ id: code, ...body })
        : await supabase.from('work_items').update(body).eq('id', editing.id)
      if (error) throw new Error(error.message)
      toast.success(isNew ? 'Item added to the schedule.' : 'Item updated.')
      setEditing(null)
      refresh()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that item')
    } finally { setBusy(false) }
  }

  if (loading) return <Loading label="Reading the schedule of rates…" />

  return (
    <div className="space-y-4">
      {picking && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Choosing a work item for the order you are writing
            </p>
            <p className="text-xs text-amber-800">
              Search, then click the line you want. You go straight back to the form with it
              filled in — <strong>everything you had typed is still there</strong>, either way.
            </p>
            <p className="mt-0.5 text-xs font-semibold text-red-700">
              Do not refresh this page or close the tab: an unsaved work order is held in this
              browser only and would be lost.
            </p>
          </div>
          {/*
            ?resume=1 is what tells the builder this is a RETURN TRIP and its
            parked draft is still wanted. Without it the guard there treats the
            draft as stale and hands back an empty form — which is exactly what
            testing found on 14-09-2026. Choosing nothing must cost nothing.
          */}
          <button className="btn-secondary btn-sm"
                  onClick={() => navigate(
                    `${backTo}${backTo!.includes('?') ? '&' : '?'}resume=1`)}>
            <ArrowLeft className="h-4 w-4" /> Back without choosing
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Schedule of Rates</h1>
          <p className="text-sm text-slate-500">
            {rows.length} work item{rows.length === 1 ? '' : 's'} · the works twin of the material master
          </p>
        </div>
        {!picking && (canEdit || isSuper) && (
          <div className="flex flex-wrap gap-2">
            {isSuper && <SorExcelButtons rows={rows} onImported={refresh} />}
            {canEdit && (
              <button className="btn-primary" onClick={openNew}>
                <Plus className="h-4 w-4" /> New work item
              </button>
            )}
          </div>
        )}
      </div>

      {!picking && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
          <HardHat className="mr-1 inline h-3.5 w-3.5" />
          Every rate here is a <strong>guide</strong>, marked INDICATIVE, and is overtaken the
          moment a work order is placed — the rate on the order is what binds.{' '}
          {canEdit
            ? 'Correct one as soon as you know the real number: the schedule is only worth reading if it is kept current.'
            : 'Procurement and the Project Head keep it current; you are reading it.'}
        </div>
      )}

      <Card title={null}>
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-sor-search className="field pl-9" value={term}
                 onChange={(e) => setTerm(e.target.value)}
                 placeholder="Item code, name, category, or how it is measured…" />
        </div>

        {shown.length === 0 ? (
          <EmptyState icon={<HardHat className="h-8 w-8" />}
                      title={term ? 'Nothing matches' : 'No work items yet'}
                      message={term
                        ? `No item mentions “${term}”. Try a trade word — brickwork, plaster, shuttering.`
                        : 'The schedule is empty.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <Th id="item_code" sort={sort} onSort={toggle}>Code</Th>
                  <Th id="item_name" sort={sort} onSort={toggle}>Item</Th>
                  <Th id="uom" sort={sort} onSort={toggle}>UOM</Th>
                  <Th id="category" sort={sort} onSort={toggle}>Category</Th>
                  <th>SAC</th>
                  <Th id="gst_rate_percent" sort={sort} onSort={toggle} align="right">GST</Th>
                  <Th id="standard_rate" sort={sort} onSort={toggle} align="right">Guide rate</Th>
                  <th>How it is measured</th>
                  {(picking || canEdit) && <th aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id}
                      onClick={picking ? () => choose(r) : undefined}
                      className={
                        flash === r.id
                          ? 'bg-emerald-200 transition-colors duration-150'
                          : picking
                            ? 'cursor-pointer transition-colors hover:bg-emerald-50'
                            : undefined}>
                    <td className="whitespace-nowrap font-mono text-xs">{r.item_code}</td>
                    <td className="max-w-[240px]">
                      {r.item_name}
                      {!r.is_active && <span className="pill-grey ml-1">retired</span>}
                    </td>
                    <td>{r.uom}</td>
                    <td className="max-w-[140px] truncate text-xs">{r.category ?? '—'}</td>
                    <td className="text-xs">{r.sac_code ?? '—'}</td>
                    <td className="num">{n(r.gst_rate_percent)}%</td>
                    <td className="num whitespace-nowrap">
                      {r.standard_rate != null ? rupees(r.standard_rate) : '—'}
                    </td>
                    <td className="max-w-[260px] truncate text-xs text-slate-500"
                        title={r.measurement_rule ?? ''}>
                      {r.measurement_rule ?? '—'}
                    </td>
                    {(picking || canEdit) && (
                      <td className="whitespace-nowrap">
                        {picking ? (
                          <span className={flash === r.id
                            ? 'inline-flex items-center gap-1 text-xs font-semibold text-emerald-800'
                            : 'text-xs text-amber-700'}>
                            {flash === r.id
                              ? <><Check className="h-3.5 w-3.5" /> Taken</>
                              : 'Choose'}
                          </span>
                        ) : (
                          <button className="btn-ghost btn-sm" onClick={() => openEdit(r)}
                                  aria-label={`Edit ${r.item_code}`}>
                            <Pencil className="h-4 w-4" /> Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ---------- the editor ---------- */}
      <Modal open={Boolean(editing)} onClose={() => setEditing(null)} wide
             title={isNew ? 'New work item' : `Edit ${editing?.item_code ?? ''}`}
             subtitle={isNew
               ? 'It joins the schedule immediately and can be used on the next work order.'
               : 'Work orders already placed keep the rate they were placed at. This changes the guide, not any contract.'}
             footer={
               <>
                 <button className="btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
                 <button className="btn-primary" disabled={busy || problems.length > 0}
                         onClick={() => void save()}>
                   {busy ? 'Saving…' : isNew ? 'Add it' : 'Save changes'}
                 </button>
               </>
             }>
        {editing && (
          <div className="space-y-4">
            {err && <Alert tone="error" title="Not saved">{err}</Alert>}

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Item code" required
                     hint={isNew
                       ? (idFromCode(editing.item_code)
                         ? `It will be filed as ${idFromCode(editing.item_code)}`
                         : 'Letters and digits, e.g. MASON-BRICK-230')
                       : `Filed as ${editing.id} — that never changes, because work orders point at it`}>
                <Input value={editing.item_code}
                       onChange={(e) => setEditing({ ...editing, item_code: e.target.value })}
                       placeholder="MASON-BRICK-230" />
              </Field>
              <Field label="Unit" required hint="From the company UOM master">
                <Select value={editing.uom} placeholder="Choose…"
                        onChange={(e) => setEditing({ ...editing, uom: e.target.value })}
                        options={uomOptions} />
              </Field>

              <Field label="Item" required className="sm:col-span-2"
                     hint="How an engineer would describe it on a measurement sheet">
                <Input value={editing.item_name}
                       onChange={(e) => setEditing({ ...editing, item_name: e.target.value })}
                       placeholder="Brickwork 230 mm in cement mortar 1:6 — LABOUR ONLY" />
              </Field>

              {/*
                PICKED, NOT TYPED. Free text here is how a master ends up with
                Masonry, masonry, Mason work and Brick Work as four different
                trades that no report can ever add together. The list is the 34
                trades a civil contract is divided into, plus every trade
                already in the table, so it can only get more complete — and
                "Add a new trade…" is there for the one that genuinely is new.
              */}
              <Field label="Trade" required hint="Groups it in the register and on every report">
                {newTrade ? (
                  <div className="flex gap-2">
                    <Input autoFocus value={editing.category ?? ''}
                           placeholder="The trade, spelled as you want it to appear everywhere"
                           onChange={(e) => setEditing({ ...editing, category: e.target.value })} />
                    <button className="btn-ghost btn-sm shrink-0"
                            onClick={() => { setNewTrade(false); setEditing({ ...editing, category: '' }) }}>
                      Use the list
                    </button>
                  </div>
                ) : (
                  <Select value={editing.category ?? ''} placeholder="Choose the trade…"
                          onChange={(e) => {
                            if (e.target.value === ADD_NEW) {
                              setNewTrade(true); setEditing({ ...editing, category: '' })
                            } else {
                              setEditing({ ...editing, category: e.target.value })
                            }
                          }}
                          options={[...tradeOptions,
                                    { value: ADD_NEW, label: '＋ Add a new trade…' }]} />
                )}
              </Field>

              <Field label="Sub-category"
                     hint={editing.category
                       ? `How it is let within ${editing.category}`
                       : 'How it is let — labour only, with material, day rate'}>
                {newSub ? (
                  <div className="flex gap-2">
                    <Input autoFocus value={editing.sub_category ?? ''}
                           onChange={(e) => setEditing({ ...editing, sub_category: e.target.value })} />
                    <button className="btn-ghost btn-sm shrink-0"
                            onClick={() => { setNewSub(false); setEditing({ ...editing, sub_category: '' }) }}>
                      Use the list
                    </button>
                  </div>
                ) : (
                  <Select value={editing.sub_category ?? ''} placeholder="Choose…"
                          onChange={(e) => {
                            if (e.target.value === ADD_NEW) {
                              setNewSub(true); setEditing({ ...editing, sub_category: '' })
                            } else {
                              setEditing({ ...editing, sub_category: e.target.value })
                            }
                          }}
                          options={[...subTradeOptions,
                                    { value: ADD_NEW, label: '＋ Add a new one…' }]} />
                )}
              </Field>

              <Field label="Nature of contract" className="sm:col-span-2"
                     hint="What this item usually is. A work order line may still override it.">
                <Select value={editing.default_nature} options={NATURE}
                        onChange={(e) => setEditing({ ...editing, default_nature: e.target.value })} />
              </Field>

              {/* A PICKER, NOT A TEXT BOX — and required.
                  It used to be free text defaulting to '9954', which is a
                  four-digit HEADING and not valid on an invoice. Since 0131
                  work_items.sac_code is NOT NULL with a foreign key into the
                  SAC master, so a typo or a blank is refused by the database;
                  far better to refuse it here, in words, than to let somebody
                  lose a form to a constraint error. */}
              <Field label="SAC code" required className="sm:col-span-2"
                     hint={editing.sac_code
                       ? (sacs as any[]).find((x) => x.code === editing.sac_code)?.description
                       : 'From the SAC master — a separate master from this schedule'}>
                <Select value={editing.sac_code ?? ''}
                        onChange={(e) => pickSac(e.target.value)}
                        placeholder="Choose a SAC code"
                        options={(sacs as any[]).map((x) => ({
                          value: x.code,
                          label: `${x.code} — ${String(x.description).slice(0, 62)}`,
                        }))} />
              </Field>
              <Field label="GST %">
                <Input type="number" step="0.01" min="0" max="100"
                       value={editing.gst_rate_percent}
                       onChange={(e) => setEditing({
                         ...editing, gst_rate_percent: Number(e.target.value) })} />
              </Field>

              {editing.default_nature === 'EQUIPMENT_HIRE' ? (
                <>
                  {/* 0214. A hire item is quoted per month or per day, and
                      billed per day: the guide day rate is worked out. */}
                  <Field label="Quoted per" required>
                    <Select value={editing.hire_rate_basis ?? ''} placeholder="Choose…"
                            onChange={(e) => setEditing({ ...editing, hire_rate_basis: e.target.value })}
                            options={[{ value: 'MONTH', label: 'Month' }, { value: 'DAY', label: 'Day' }]} />
                  </Field>
                  <Field label="How it is logged" className="sm:col-span-2"
                         hint={editing.hire_by_pieces
                           ? 'Rate is per piece per day. The site records deliveries and returns on challans.'
                           : 'Rate is per machine. The site marks each machine every day: worked, idle, holiday or breakdown.'}>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                             checked={Boolean(editing.hire_by_pieces)}
                             onChange={(e) => setEditing({
                               ...editing, hire_by_pieces: e.target.checked,
                               ...(e.target.checked ? { hire_rate_basis: 'DAY' } : {}) })} />
                      <span>Hired by the piece (formwork, props, scaffolding)</span>
                    </label>
                  </Field>
                  <Field label="Quoted hire rate" required
                         hint={n(editing.hire_quoted_rate) > 0 && editing.hire_rate_basis
                           ? `Guide day rate ${rupees(Math.round(n(editing.hire_quoted_rate)
                               / (editing.hire_rate_basis === 'MONTH' ? 30 : 1) * 100) / 100)}`
                             + (editing.hire_rate_basis === 'MONTH' ? ' (a month is 30 days)' : '')
                           : 'Indicative only, for negotiating against.'}>
                    <Input type="number" step="0.01" min="0"
                           value={editing.hire_quoted_rate ?? ''}
                           onChange={(e) => setEditing({
                             ...editing,
                             hire_quoted_rate: e.target.value === '' ? null : Number(e.target.value) })} />
                  </Field>
                </>
              ) : (
                <Field label="Guide rate" className="sm:col-span-2"
                       hint="Indicative only, for negotiating against. Leave blank if you have no view.">
                  <Input type="number" step="0.01" min="0"
                         value={editing.standard_rate ?? ''}
                         onChange={(e) => setEditing({
                           ...editing,
                           standard_rate: e.target.value === '' ? null : Number(e.target.value) })} />
                </Field>
              )}

              <Field label="How it is measured" required className="sm:col-span-2"
                     hint="Printed on the work order. Say what is included in the quantity and what is deducted.">
                <Textarea rows={3} value={editing.measurement_rule ?? ''}
                          onChange={(e) => setEditing({
                            ...editing, measurement_rule: e.target.value })}
                          placeholder="Solid wall area. Deduct openings over 0.5 SQM. No deduction for bearings." />
              </Field>

              <Field label="Notes" className="sm:col-span-2"
                     hint="Anything a buyer should know. Not printed.">
                <Textarea rows={2} value={editing.description ?? ''}
                          onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </Field>

              <Field label="In use" className="sm:col-span-2"
                     hint="Retire an item rather than deleting it — work orders already placed point at it.">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300"
                         checked={editing.is_active}
                         onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
                  <span>Offer this item on new work orders</span>
                  {!editing.is_active && <span className="pill-grey">retired</span>}
                </label>
              </Field>
            </div>

            {problems.length > 0 && (
              <ul className="space-y-1 rounded-lg bg-amber-50 px-3 py-2">
                {problems.map((p, i) => (
                  <li key={i} className="text-xs text-amber-900">• {p}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
